import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Button,
  DataGrid,
  DatePicker,
  Input,
  messageFromError,
  toast,
  unwrapAction,
  useGridConfig,
  useGridRows,
} from '@eo/shared';

import type { HolidayItem } from './api/types';
import { makeHolidaysGridConfig } from './config/gridConfig';
import type { HolidaysTabProps } from './types';

export function HolidaysTab({ holidays, year, onYearShift, onCreate, onRemove }: HolidaysTabProps) {
  const [draft, setDraft] = useState({ country: 'US', date: '', name: '' });
  const [busy, setBusy] = useState(false);

  const run = async (work: () => Promise<void>, failure: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await work();
    } catch (err) {
      toast.error(messageFromError(err, failure));
    } finally {
      setBusy(false);
    }
  };

  const config = useGridConfig(makeHolidaysGridConfig, {
    onRemove: (row: HolidayItem) =>
      void run(async () => {
        unwrapAction(await onRemove({ id: row.id }));
        toast.success('Holiday removed.');
      }, 'Removing the holiday failed. Try again.'),
  });
  const rows = useGridRows(holidays, config);

  return (
    <section className="flex flex-col gap-4">
      <p className="text-xs text-text-tertiary">
        Org-wide, and applied per country through each member&apos;s country field — so a holiday
        belongs to the calendar rather than to any one person.
      </p>

      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!draft.date || !draft.name) return;
          void run(async () => {
            unwrapAction(await onCreate({ ...draft, country: draft.country.toUpperCase() }));
            setDraft({ country: draft.country, date: '', name: '' });
            toast.success('Holiday added.');
          }, 'Adding the holiday failed. Try again.');
        }}
      >
        <Input
          value={draft.country}
          onChange={(e) => setDraft((p) => ({ ...p, country: e.target.value }))}
          aria-label="Holiday country"
          className="w-20"
          maxLength={2}
        />
        <DatePicker
          value={draft.date}
          onChange={(value) => setDraft((p) => ({ ...p, date: value }))}
          ariaLabel="Holiday date"
          className="w-40"
        />
        <Input
          value={draft.name}
          onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
          placeholder="Holiday name"
          aria-label="Holiday name"
          className="w-56"
        />
        <Button type="submit" disabled={busy || !draft.date || !draft.name}>
          Add holiday
        </Button>
      </form>

      <DataGrid<HolidayItem>
        config={config}
        rows={rows.rows}
        pagination={rows.pagination}
        headerActions={
          // The list is year-scoped, so the year steps beside the grid it filters
          // rather than from the page chrome, where it would look global.
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onYearShift(-1)}
              aria-label="Previous year"
            >
              <ChevronLeft className="size-4" />
            </Button>
            <span className="min-w-16 text-center text-sm font-medium text-text-primary">
              {year}
            </span>
            <Button variant="ghost" size="sm" onClick={() => onYearShift(1)} aria-label="Next year">
              <ChevronRight className="size-4" />
            </Button>
          </div>
        }
      />
    </section>
  );
}
